import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { BASE_URL } from "../../config/api";
import "./../Styles/login.css";

export default function Register({ isOpen = true, onClose = () => {} }) {
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    name: "",
    mobileno: "",
    password: "",
    address: "",
    city: "",
    emailid: "",
    countrycode: "91",
  });

  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // =========================================================
  // OTP TIMER
  // =========================================================

  const [timer, setTimer] = useState(60);

  const [canResend, setCanResend] = useState(false);

  // =========================================================
  // OTP COUNTDOWN
  // =========================================================

  useEffect(() => {
    if (step !== 2) {
      return;
    }

    if (timer <= 0) {
      setCanResend(true);
      return;
    }

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setCanResend(true);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [step, timer]);

  // =========================================================
  // PHONE VALIDATION
  // =========================================================

  const validatePhone = () => {
    const phone = formData.mobileno;

    if (formData.countrycode === "91") {
      return /^[6-9]\d{9}$/.test(phone);
    }

    return phone.length >= 6;
  };

  // =========================================================
  // SEND OTP
  // =========================================================

  const sendOtp = async () => {
    setErrors({});
    setMessage("");

    try {
      setLoading(true);

      // Generate OTP
      const otpValue = Math.floor(100000 + Math.random() * 900000).toString();

      const params = new URLSearchParams();

      params.append("mobileno", formData.mobileno);
      params.append("otp", otpValue);
      params.append("code", formData.countrycode);

      const response = await fetch(`${BASE_URL}/send_otp.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params.toString(),
      });

      const text = await response.text();

      let result;

      try {
        result = JSON.parse(text);
      } catch (error) {
        setErrors({
          api: "Invalid server response",
        });

        return false;
      }

      if (result.success === true || result.status === true) {
        // Save OTP
        localStorage.setItem("registerOtp", otpValue);

        // Reset timer
        setTimer(60);
        setCanResend(false);

        // Clear previous OTP
        setOtp("");

        setMessage("OTP sent successfully");

        return true;
      }

      setErrors({
        api: result.message || "Failed to send OTP",
      });

      return false;
    } catch (error) {
      console.error("SEND OTP ERROR:", error);

      setErrors({
        api: "Server Error",
      });

      return false;
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // STEP 1
  // VALIDATE + SEND OTP
  // =========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setErrors({});
    setMessage("");

    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!validatePhone()) {
      newErrors.mobileno = "Enter valid mobile number";
    }

    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (
      formData.emailid &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailid)
    ) {
      newErrors.emailid = "Enter valid email";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    const sent = await sendOtp();

    if (sent) {
      setStep(2);
    }
  };

  // =========================================================
  // VERIFY OTP
  // =========================================================

  const verifyOtp = async () => {
    setErrors({});
    setMessage("");

    if (!otp.trim()) {
      setErrors({
        otp: "Enter OTP",
      });

      return;
    }

    if (otp.length !== 6) {
      setErrors({
        otp: "Enter valid 6 digit OTP",
      });

      return;
    }

    const savedOtp = localStorage.getItem("registerOtp");

    if (!savedOtp) {
      setErrors({
        otp: "OTP expired. Please request a new OTP.",
      });

      return;
    }

    if (otp.trim() !== savedOtp) {
      setErrors({
        otp: "Invalid OTP",
      });

      return;
    }

    try {
      setLoading(true);

      setMessage("OTP verified successfully ✔");

      // =====================================================
      // REGISTER USER
      // =====================================================

      const reg = new FormData();

      reg.append("name", formData.name);

      reg.append("mobileno", formData.mobileno);

      reg.append("password", formData.password);

      reg.append("address", formData.address);

      reg.append("city", formData.city);

      reg.append("emailid", formData.emailid);

      reg.append("countrycode", formData.countrycode);

      const regRes = await fetch(`${BASE_URL}/register.php`, {
        method: "POST",
        body: reg,
      });

      const regText = await regRes.text();

      let regData;

      try {
        regData = JSON.parse(regText);
      } catch (error) {
        setErrors({
          api: "Invalid registration server response",
        });

        return;
      }

      if (regData.success === false || regData.status === false) {
        setErrors({
          api: regData.message || "Registration failed",
        });

        return;
      }

      localStorage.removeItem("registerOtp");

      setMessage("Account created successfully 🎉");

      setStep(3);
    } catch (error) {
      console.error("REGISTER ERROR:", error);

      setErrors({
        api: "Server Error",
      });
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // RESEND OTP
  // =========================================================

  const resendOtp = async () => {
    if (!canResend || loading) {
      return;
    }

    setErrors({});
    setMessage("");

    const sent = await sendOtp();

    if (sent) {
      setMessage("New OTP sent successfully");
    }
  };

  // =========================================================
  // CLOSE
  // =========================================================

  const handleClose = () => {
    localStorage.removeItem("registerOtp");

    setStep(1);
    setOtp("");
    setTimer(60);
    setCanResend(false);
    setMessage("");
    setErrors({});
    setLoading(false);

    onClose();
  };

  // =========================================================
  // FORMAT TIMER
  // =========================================================

  const formatTimer = () => {
    const minutes = Math.floor(timer / 60);

    const seconds = timer % 60;

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0",
    )}`;
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="login-popup-overlay">
      <div className="login-popup-card register-popup-card">
        {/* CLOSE */}
        <button className="login-popup-close" onClick={handleClose}>
          ✕
        </button>

        {/* =================================================
            STEP 1
        ================================================= */}

        {step === 1 && (
          <form onSubmit={handleSubmit}>
            <div className="login-header">
              <h2>Create Account</h2>
              {/* <p>Register New Account</p> */}
            </div>

            {/* NAME */}
            <div className="login-popup-form-group">
              <label>Full Name</label>

              <input
                type="text"
                className="login-popup-input"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    name: e.target.value,
                  })
                }
              />

              {errors.name && (
                <div className="login-popup-error">{errors.name}</div>
              )}
            </div>

            {/* MOBILE */}
            <div className="login-popup-form-group">
              <label>Mobile Number</label>

              <PhoneInput
                country="in"
                enableSearch
                value={`${formData.countrycode}${formData.mobileno}`}
                onChange={(phone, country) => {
                  const dialCode = country.dialCode;

                  let mobile = phone;

                  if (phone.startsWith(dialCode)) {
                    mobile = phone.slice(dialCode.length);
                  }

                  setFormData({
                    ...formData,
                    mobileno: mobile,
                    countrycode: dialCode,
                  });
                }}
                inputStyle={{
                  width: "100%",
                  height: "45px",
                }}
              />

              {errors.mobileno && (
                <div className="login-popup-error">{errors.mobileno}</div>
              )}
            </div>
            <div className="login-popup-form-group">
              <label>Password</label>

              <div className="register-password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  className="login-popup-input"
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      password: e.target.value,
                    })
                  }
                />

                <i
                  className={`bi ${
                    showPassword ? "bi-eye-slash" : "bi-eye"
                  } register-password-icon`}
                  onClick={() => setShowPassword(!showPassword)}
                />
              </div>

              {errors.password && (
                <div className="login-popup-error">{errors.password}</div>
              )}
            </div>
            <div className="login-popup-form-group">
              <label>Address</label>

              <textarea
                className="login-popup-input register-address"
                placeholder="Enter your address"
                value={formData.address}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    address: e.target.value,
                  })
                }
              />
            </div>
            <div className="login-popup-form-group">
              <label>City</label>

              <input
                type="text"
                className="login-popup-input"
                placeholder="Enter your city"
                value={formData.city}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    city: e.target.value,
                  })
                }
              />
            </div>

            {/* EMAIL */}
            <div className="login-popup-form-group">
              <label>Email</label>

              <input
                type="email"
                className="login-popup-input"
                placeholder="Enter your email"
                value={formData.emailid}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    emailid: e.target.value,
                  })
                }
              />

              {errors.emailid && (
                <div className="login-popup-error">{errors.emailid}</div>
              )}
            </div>

            {errors.api && (
              <div className="login-popup-error">{errors.api}</div>
            )}

            {message && (
              <div className="register-success-message">{message}</div>
            )}

            <button
              type="submit"
              className="login-popup-btn"
              disabled={loading}
            >
              {loading ? "Sending OTP..." : "Create Account"}
            </button>
          </form>
        )}

        {/* =================================================
            STEP 2 - OTP
        ================================================= */}

        {step === 2 && (
          <div className="register-otp-wrapper">
            <div className="register-otp-icon">
              <i className="bi bi-shield-lock-fill"></i>
            </div>

            <div className="login-header register-otp-header">
              <h2>Verify OTP</h2>

              <p>We sent a verification code to</p>

              <strong className="register-phone-number">
                +{formData.countrycode} {formData.mobileno}
              </strong>
            </div>

            {/* OTP INPUT */}
            <div className="register-otp-input-group">
              <label>Enter 6 Digit OTP</label>

              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                className="register-otp-input"
                placeholder="••••••"
                value={otp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");

                  setOtp(value);

                  setErrors({});
                }}
              />

              {errors.otp && (
                <div className="login-popup-error register-otp-error">
                  {errors.otp}
                </div>
              )}
            </div>

            {/* TIMER */}
            <div className="register-otp-timer">
              {!canResend ? (
                <>
                  <span>OTP expires in</span>

                  <strong>{formatTimer()}</strong>
                </>
              ) : (
                <span className="register-otp-expired">OTP expired</span>
              )}
            </div>

            {/* VERIFY */}
            <button
              type="button"
              className="login-popup-btn register-verify-btn"
              onClick={verifyOtp}
              disabled={loading || otp.length !== 6}
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>

            {/* RESEND */}
            <div className="register-resend-area">
              <span>Didn't receive OTP?</span>

              <button
                type="button"
                className={
                  canResend
                    ? "register-resend-btn active"
                    : "register-resend-btn"
                }
                onClick={resendOtp}
                disabled={!canResend || loading}
              >
                Resend OTP
              </button>
            </div>

            {/* MESSAGE */}
            {message && (
              <div className="register-success-message">{message}</div>
            )}

            {errors.api && (
              <div className="login-popup-error">{errors.api}</div>
            )}

            {/* CHANGE NUMBER */}
            <button
              type="button"
              className="register-change-number"
              onClick={() => {
                localStorage.removeItem("registerOtp");

                setStep(1);
                setOtp("");
                setTimer(60);
                setCanResend(false);
                setErrors({});
                setMessage("");
              }}
            >
              <i className="bi bi-arrow-left"></i> Change Mobile Number
            </button>
          </div>
        )}

        {/* =================================================
            STEP 3
        ================================================= */}

        {step === 3 && (
          <div className="register-success-wrapper">
            <div className="register-success-icon">
              <i className="bi bi-check-lg"></i>
            </div>

            <h2>Account Created!</h2>

            <p>Your account has been created successfully.</p>

            <button
              type="button"
              className="login-popup-btn"
              onClick={handleClose}
            >
              Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
